<script setup lang="ts">
/**
 * 🅲🅾🅼🅿🅾🅽🅴🅽🆃 [ChooseDevice]
 * Модальное окно / экран "Выбор устройства"
 */

import { computed, inject, ref, useCssModule, watch } from "vue";

import type { I_WIDGET_STEPS } from "@/stores/tiw/tiw.types";

import Modal from "@/components/modal/modal.vue";
import Notification from "@/components/notification/notification.vue";
import StepsButtons from "@/components/StepsButtons/StepsButtons.vue";
import { usePostQuestDevice } from "@/hooks/usePostQuestDevice/usePostQuestDevice";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { isChooseNewDeviceStepAvailableKey } from "@/keys/settings";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useTIWStore } from "@/stores/tiw/tiw";
// import Button from "@/ui/button/button.vue";
import Field from "@/ui/field/field.vue";
import Select from "@/ui/select/select.vue";

const classes = useCssModule();

const TIWStore = useTIWStore();
const ChooseDeviceStore = useChooseDeviceStore();

const isChooseNewDeviceStepAvailable = inject(isChooseNewDeviceStepAvailableKey);

const nextStep: I_WIDGET_STEPS = isChooseNewDeviceStepAvailable
    ? "chooseDeviceToBuy"
    : "howYouRateDevice";

const loadingToNext = ref<boolean>(false);

watch(ChooseDeviceStore.deviceChain, () => {
    ChooseDeviceStore.setIsDeviceNotAccepted(false);
});

watch(
    computed(() => ChooseDeviceStore.deviceChain.join(", ")),
    () => {
        ChooseDeviceStore.setIsDeviceNotAccepted(false);
    },
);

async function clickToNext() {
    if (isChooseNewDeviceStepAvailable) {
        TIWStore.switchStep(nextStep);
    } else {
        loadingToNext.value = true;

        await usePostQuestDevice();

        loadingToNext.value = false;

        if (ChooseDeviceStore.isDeviceNotAccepted === false) {
            TIWStore.switchStep(nextStep);
        }
    }
}

const disabledCategorySelect = computed<boolean>(
    (): boolean => !ChooseDeviceStore.field_brand.value,
);
const disabledModelSelect = computed<boolean>(
    (): boolean => !ChooseDeviceStore.field_category.value,
);
const disabledCPUSelect = computed<boolean>((): boolean => !ChooseDeviceStore.field_model.value);
const disabledGPUSelect = computed<boolean>((): boolean => !ChooseDeviceStore.field_CPU.value);
const disabledRAMSelect = computed<boolean>((): boolean => !ChooseDeviceStore.field_GPU.value);
const disabledMemorySelect = computed<boolean>((): boolean => !ChooseDeviceStore.field_RAM.value);
const disabledStorageSelect = computed<boolean>(
    (): boolean => !ChooseDeviceStore.field_model.value,
);
const disabledColorSelect = computed<boolean>(
    (): boolean => !ChooseDeviceStore.field_storage.value,
);

const terms = computed<string>(() => {
    if (TIWStore.INJECTED_TIW.current_settings.domain === "comfy.ua") {
        return "";
    }

    if (TIWStore.INJECTED_TIW.current_settings.domain === "breezy.pl") {
        return "";
    }

    if (TIWStore.INJECTED_TIW.current_settings.domain === "foxtrot.com.ua") {
        return useTranslate("tiw__chooseDevice__terms__ua_foxtrot");
    }

    if (TIWStore.INJECTED_TIW.current_settings.domain === "breezy.ua") {
        return useTranslate("tiw__chooseDevice__terms__ua_breezy");
    }

    if (TIWStore.INJECTED_TIW.current_settings.domain === "avo.africa") {
        return useTranslate("tiw__chooseDevice__terms__za_avo_africa");
    }

    if (TIWStore.INJECTED_TIW.current_settings.domain === "dg.co.za") {
        return useTranslate("tiw__chooseDevice__terms__za_dg_co");
    }

    return useTranslate("tiw__chooseDevice__terms");
});
</script>

<template>
    <Modal data-component="chooseDevice" class="tiw__step__chooseDevice">
        <template #header>
            <p class="tiw__h2">
                {{ useTranslate("tiw__chooseDevice__title") }}
            </p>
            <p>{{ useTranslate("tiw__chooseDevice__subtitle") }}</p>
        </template>

        <div class="tiw__step__chooseDevice__body">
            <form name="chooseDeviceForm">
                <!-- Поле "Бренд" -->
                <Field class="tiw__field_brand">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__brand") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_brand.value"
                            :options="ChooseDeviceStore.brands"
                            label="name"
                            name="brand"
                            :disabled="loadingToNext"
                        />
                    </template>
                </Field>

                <!-- Поле "Категория" -->
                <Field class="tiw__field_category">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__category") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_category.value"
                            :options="ChooseDeviceStore.field_category.data"
                            name="category"
                            label="name"
                            :disabled="loadingToNext || disabledCategorySelect"
                        />
                    </template>
                </Field>

                <!-- Поле "Модель" -->
                <Field class="tiw__field_model">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__model") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_model.value"
                            :options="ChooseDeviceStore.field_model.data"
                            :is-loading="ChooseDeviceStore.isLoading.model"
                            name="model"
                            label="name"
                            :disabled="loadingToNext || disabledModelSelect"
                        />
                    </template>
                </Field>

                <!-- Поле "Память / Модель / Год" -->
                <Field v-if="!ChooseDeviceStore.isCommercialNotebook" class="tiw__field_other">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__other") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_storage.value"
                            :is-loading="ChooseDeviceStore.isLoading.product"
                            :options="ChooseDeviceStore.field_storage.data"
                            name="other"
                            label="storage"
                            :disabled="loadingToNext || disabledStorageSelect"
                        />
                    </template>
                </Field>

                <!-- Поле "Цвет" -->
                <Field v-if="!ChooseDeviceStore.isCommercialNotebook" class="tiw__field_color">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__color") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_color.value"
                            :options="ChooseDeviceStore.field_color.data"
                            name="color"
                            label="color"
                            :disabled="loadingToNext || disabledColorSelect"
                        />
                    </template>
                </Field>
                <Field v-if="ChooseDeviceStore.isCommercialNotebook" class="tiw__field_color">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__CPU") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_CPU.value"
                            :options="ChooseDeviceStore.field_CPU.data"
                            name="keyShow2"
                            label="keyShow2"
                            :disabled="loadingToNext || disabledCPUSelect"
                            :is-loading="ChooseDeviceStore.isLoading.product"
                        />
                    </template>
                </Field>
                <Field v-if="ChooseDeviceStore.isCommercialNotebook" class="tiw__field_color">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__GPU") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_GPU.value"
                            :options="ChooseDeviceStore.field_GPU.data"
                            name="keyShow3"
                            label="keyShow3"
                            :disabled="loadingToNext || disabledGPUSelect"
                        />
                    </template>
                </Field>
                <Field v-if="ChooseDeviceStore.isCommercialNotebook" class="tiw__field_color">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__RAM") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_RAM.value"
                            :options="ChooseDeviceStore.field_RAM.data"
                            name="keyShow4"
                            label="keyShow4"
                            :disabled="loadingToNext || disabledRAMSelect"
                        />
                    </template>
                </Field>
                <Field v-if="ChooseDeviceStore.isCommercialNotebook" class="tiw__field_color">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__chooseDevice__Memory") }}
                        </span>
                    </template>
                    <template #body>
                        <Select
                            v-model="ChooseDeviceStore.field_Memory.value"
                            :options="ChooseDeviceStore.field_Memory.data"
                            name="keyShow5"
                            label="keyShow5"
                            :disabled="loadingToNext || disabledMemorySelect"
                        />
                    </template>
                </Field>
            </form>
            <div
                v-if="ChooseDeviceStore.isDeviceNotAccepted"
                class="tiw__step__chooseDevice__deviceNotAccepted"
            >
                <Notification type="danger">
                    {{ useTranslate("tiw__chooseDevice_deviceNotAccepted") }}
                </Notification>
            </div>

            <!-- <Button
                type="button"
                :disabled="!ChooseDeviceStore.isAcceptedNext || loadingToNext"
                :loading="loadingToNext"
                class="tiw__step__chooseDevice__btn-next tiw__btn-next_manual"
                @click="clickToNext"
            >
                <span>{{ useTranslate("tiw__continue") }}</span>
            </Button> -->

            <StepsButtons
                no-back-btn
                :disabled="!ChooseDeviceStore.isAcceptedNext || loadingToNext"
                :loading="loadingToNext"
                :click-to-next="clickToNext"
                :class="classes.stepsButtons"
            />

            <p v-if="terms" class="tiw__text tiw_fs-12 tiw_tc tiw_usn" v-html="terms" />
        </div>
    </Modal>
</template>

<style lang="scss" src="./chooseDevice.scss"></style>
<style module>
.stepsButtons {
    margin-bottom: 40px;
}
</style>
